import { useDashReport } from '../hooks/useDashReport'
import { HorizontalBarChart } from '../components'

interface DesgloseEspecialidad {
  especialidad: string
  total: number
  encontrados: number
  no_encontrados: number
  porcentaje: number
}

interface DesgloseRegion {
  region: string
  total: number
  encontrados: number
  no_encontrados: number
  porcentaje: number
}

interface PanelEnriquecidoReport {
  titulo: string
  subtitulo: string
  kpis: {
    total: number
    encontrados: number
    no_encontrados: number
    pct_match: number
  }
  por_especialidad: DesgloseEspecialidad[]
  por_region: DesgloseRegion[]
}

const fmt = (n: number) => n.toLocaleString('es-EC')

export function PanelEnriquecidoPage() {
  const { data, isLoading, error } = useDashReport<PanelEnriquecidoReport>('panel-enriquecido')

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
        </div>
      </div>
    )
  }

  const { kpis, por_especialidad, por_region } = data

  const chartEspecialidades = por_especialidad.map(e => ({
    label: e.especialidad,
    value: e.total,
    color: '#57a6d8',
  }))
  const chartRegiones = por_region.map(r => ({
    label: r.region,
    value: r.total,
    color: '#8d82bc',
  }))

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600 flex items-center gap-2">
                  <span>🇪🇨</span> {data.subtitulo}
                </p>
              </div>
            </div>
            <a
              href="https://enterprise.tonalstrategies.com/pricing/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              Calculador
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        {/* KPIs de enriquecimiento */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm px-4 py-5 text-center">
            <p className="text-3xl font-bold text-gray-900">{fmt(kpis.total)}</p>
            <p className="text-sm text-gray-600 mt-1">Total en panel</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm px-4 py-5 text-center">
            <p className="text-3xl font-bold text-green-600">{fmt(kpis.encontrados)}</p>
            <p className="text-sm text-gray-600 mt-1">Encontrados en base</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm px-4 py-5 text-center">
            <p className="text-3xl font-bold text-pink-600">{fmt(kpis.no_encontrados)}</p>
            <p className="text-sm text-gray-600 mt-1">No encontrados</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm px-4 py-5 text-center">
            <p className="text-3xl font-bold text-blue-600">{kpis.pct_match}%</p>
            <p className="text-sm text-gray-600 mt-1">Match con la base</p>
          </div>
        </div>

        {/* Gráficas de distribución */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              Distribución por Especialidad
            </h3>
            <HorizontalBarChart data={chartEspecialidades} />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
              <svg className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Distribución por Región
            </h3>
            <HorizontalBarChart data={chartRegiones} />
          </div>
        </div>

        {/* Tabla por especialidad */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
            <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            Detalle por Especialidad
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white text-xs lg:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-left font-semibold text-gray-700">Especialidad</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Total</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Encontrados</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">No encontrados</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">% del panel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {por_especialidad.map(e => (
                  <tr key={e.especialidad} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{e.especialidad}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{fmt(e.total)}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-green-600 font-medium">{fmt(e.encontrados)}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-pink-600">{fmt(e.no_encontrados)}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{e.porcentaje}%</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                  <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{fmt(kpis.total)}</td>
                  <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-green-600">{fmt(kpis.encontrados)}</td>
                  <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-pink-600">{fmt(kpis.no_encontrados)}</td>
                  <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-blue-600">{kpis.pct_match}%</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Tabla por región */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
            <svg className="w-5 h-5 text-purple-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Detalle por Región
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white text-xs lg:text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-left font-semibold text-gray-700">Región</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Total</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">Encontrados</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">No encontrados</th>
                  <th className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-gray-700">% del panel</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {por_region.map(r => (
                  <tr key={r.region} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{r.region}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{fmt(r.total)}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-green-600 font-medium">{fmt(r.encontrados)}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-pink-600">{fmt(r.no_encontrados)}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{r.porcentaje}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies — Panel Enriquecido</p>
        </div>

      </div>
    </div>
  )
}
